import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lightbulb, X, ArrowRight } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

/**
 * GuideNudge — subtle contextual hint shown when a user jumps ahead
 * in the onboarding flow without the prerequisite step completed.
 *
 * Rules:
 *  - Non-blocking (does NOT prevent the user from doing their action)
 *  - Shows once per nudge per 24 hours (localStorage-backed dismissals)
 *  - Auto-hides the moment the prerequisite is completed
 *  - Only renders for authenticated users
 *  - Reads progress from the existing /api/explore/progress endpoint (cached)
 */

// Rule table: if user is on `path_match` AND `requires` item isn't complete,
// show this nudge. Ordered by priority — first match wins per route.
const NUDGE_RULES = [
  {
    id: 'needs_location_for_gdd',
    match: (p) => p.startsWith('/app') || p.startsWith('/calendar') || p.startsWith('/my-plan'),
    requires: 'location',
    title: 'Set your location first',
    body: 'GDD tracking, weather, and seasonal timing all need your zip code. Takes 10 seconds.',
    cta_label: 'Set location',
    cta_path: '/app',
  },
  {
    id: 'needs_property_for_irrigation',
    match: (p) => p.startsWith('/irrigation'),
    requires: 'property',
    title: 'Your sprinkler data ties to zones',
    body: 'Build your property first so watering math runs per zone — front yard vs. back vs. side.',
    cta_label: 'Build property',
    cta_path: '/property',
  },
  {
    id: 'needs_property_for_inspections',
    match: (p) => p.startsWith('/inspections'),
    requires: 'property',
    title: 'Linking inspections to zones unlocks tracking',
    body: 'You can still log an inspection, but tagging it to a zone builds resolution history per area over time.',
    cta_label: 'Set up zones',
    cta_path: '/property',
  },
  {
    id: 'needs_property_for_smartplan',
    match: (p) => p.startsWith('/my-plan'),
    requires: 'property',
    title: 'Smart Plan personalizes with your zones',
    body: 'The AI plan leans on your zones + GDD + activity. Build your property to get recommendations tailored to your lawn.',
    cta_label: 'Build property',
    cta_path: '/property',
  },
  {
    id: 'needs_zone_size_for_calculator',
    match: (p) => p.startsWith('/calculator'),
    requires: 'zone_size',
    title: 'Record zone sqft for exact rates',
    body: 'The mix calculator works standalone, but recording each zone\'s size means every spray log auto-calculates coverage.',
    cta_label: 'Add zone size',
    cta_path: '/property',
  },
  {
    id: 'needs_property_for_measure',
    match: (p) => p.startsWith('/measure'),
    requires: 'property',
    title: 'Build your property first',
    body: 'Satellite measurements save into your properties and zones — set those up so your work is organized.',
    cta_label: 'Build property',
    cta_path: '/property',
  },
];

const DISMISS_WINDOW_MS = 24 * 60 * 60 * 1000; // 24h — nudge comes back next day if still relevant

export const GuideNudge = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [dismissedMap, setDismissedMap] = useState({});

  // Use _id (MongoDB) or id as the dismissal key
  const userKey = user?._id || user?.id;

  // Load dismissals from localStorage (keyed per user)
  useEffect(() => {
    if (!userKey) return;
    try {
      const raw = localStorage.getItem(`turfpulse_nudge_dismissed_${userKey}`);
      if (raw) setDismissedMap(JSON.parse(raw));
    } catch {
      // ignore
    }
  }, [userKey]);

  // Fetch onboarding progress; refetch on route change to catch just-completed items
  useEffect(() => {
    if (!userKey) { setProgress(null); return; }
    axios
      .get(`${API_URL}/api/explore/progress`, { withCredentials: true })
      .then((res) => setProgress(res.data))
      .catch(() => {});
  }, [userKey, location.pathname]);

  if (!user || !progress) return null;

  // Find the first matching nudge for the current path where the prerequisite isn't done
  const nudge = NUDGE_RULES.find((rule) => {
    if (!rule.match(location.pathname)) return false;
    const prereq = progress.items?.find((i) => i.id === rule.requires);
    if (!prereq || prereq.completed) return false;
    // Respect dismissal window
    const dismissedAt = dismissedMap[rule.id];
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_WINDOW_MS) return false;
    // Don't nudge a user back to the same page they're already on
    if (location.pathname === rule.cta_path) return false;
    return true;
  });

  if (!nudge) return null;

  const handleDismiss = () => {
    const updated = { ...dismissedMap, [nudge.id]: Date.now() };
    setDismissedMap(updated);
    try {
      localStorage.setItem(`turfpulse_nudge_dismissed_${userKey}`, JSON.stringify(updated));
    } catch {
      // ignore
    }
  };

  const handleCta = () => {
    handleDismiss();
    navigate(nudge.cta_path);
  };

  return (
    <div
      className="bg-primary/5 border-b border-primary/20"
      data-testid={`guide-nudge-${nudge.id}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <div className="shrink-0 w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center">
          <Lightbulb size={14} weight="fill" className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-medium text-foreground truncate">
            {nudge.title}
          </p>
          <p className="text-[11px] sm:text-xs text-muted-foreground leading-snug line-clamp-1 sm:line-clamp-none">
            {nudge.body}
          </p>
        </div>
        <button
          onClick={handleCta}
          className="shrink-0 text-xs font-semibold text-primary hover:text-primary/80 inline-flex items-center gap-1 whitespace-nowrap"
          data-testid={`guide-nudge-cta-${nudge.id}`}
        >
          {nudge.cta_label}
          <ArrowRight size={12} weight="bold" />
        </button>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="shrink-0 w-6 h-6 rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-muted inline-flex items-center justify-center"
          data-testid={`guide-nudge-dismiss-${nudge.id}`}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default GuideNudge;
